import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicHeader from '../components/PublicHeader';
import { useBilling } from '../billing';

type Currency = 'USD' | 'GBP' | 'EUR' | 'BRL';

interface CurrencyConfig {
  symbol: string;
  starterMonthly: number;
  starterAnnualMonthly: number;
  starterAnnualYearly: number;
  proMonthly: number;
  proAnnualMonthly: number;
  proAnnualYearly: number;
}

const CURRENCY_CONFIG: Record<Currency, CurrencyConfig> = {
  USD: {
    symbol: '$',
    starterMonthly: 25,
    starterAnnualMonthly: 18.75,
    starterAnnualYearly: 225,
    proMonthly: 45,
    proAnnualMonthly: 33.75,
    proAnnualYearly: 405,
  },
  GBP: {
    symbol: '£',
    starterMonthly: 19.99,
    starterAnnualMonthly: 14.99,
    starterAnnualYearly: 179.88,
    proMonthly: 35.99,
    proAnnualMonthly: 26.99,
    proAnnualYearly: 323.88,
  },
  EUR: {
    symbol: '€',
    starterMonthly: 23.99,
    starterAnnualMonthly: 17.99,
    starterAnnualYearly: 215.88,
    proMonthly: 42.99,
    proAnnualMonthly: 31.99,
    proAnnualYearly: 383.88,
  },
  BRL: {
    symbol: 'R$',
    starterMonthly: 99.90,
    starterAnnualMonthly: 74.90,
    starterAnnualYearly: 898.80,
    proMonthly: 179.90,
    proAnnualMonthly: 134.90,
    proAnnualYearly: 1618.80,
  },
};

const CURRENCY_LABELS: Record<Currency, string> = {
  USD: 'USD ($)',
  GBP: 'GBP (£)',
  EUR: 'EUR (€)',
  BRL: 'BRL (R$)',
};

const CURRENCY_STORAGE_KEY = 'teh_preferred_currency';

function formatPrice(symbol: string, n: number): string {
  return n % 1 === 0 ? `${symbol}${n}` : `${symbol}${n.toFixed(2)}`;
}

type Plan = {
  name: string;
  description: string;
  badge?: string;
  trialBadge?: string;
  features: string[];
  cta: string;
  planKey?: string;
  highlighted: boolean;
};

const PLANS: Plan[] = [
  {
    name: 'Starter',
    description: 'For individuals who want a cleaner, calmer inbox.',
    trialBadge: '7-day free trial',
    features: [
      'Connect 1 inbox',
      'Subscription cleanup & bulk archive',
      'Draft replies in your voice',
      'Meeting scheduling across calendars & time zones',
    ],
    cta: 'Start free trial',
    planKey: 'starter',
    highlighted: false,
  },
  {
    name: 'Pro',
    description: 'For power users who want The Email Helper to run their inbox.',
    badge: 'Most popular',
    trialBadge: '7-day free trial',
    features: [
      'Everything in Starter',
      'Connect up to 3 inboxes',
      'Invoice archiving',
      'Critical person identification — prioritise your boss & key customers',
      'Quick-action emails — AI suggestions, no dashboard needed',
      'Advanced "Get Organised" dashboard',
      'Custom auto-labels',
      'Google Drive & OneDrive attachment organisation',
    ],
    cta: 'Start free trial',
    planKey: 'pro',
    highlighted: true,
  },
  {
    name: 'Enterprise',
    description: 'For teams who need The Email Helper at scale.',
    features: [
      'Everything in Pro',
      'Unlimited inboxes',
      'Smarter AI — enhanced models & custom fine-tuning',
      'Live one-on-one onboarding & training',
      'Priority support with guaranteed SLA',
      'Automated team setup with SSO & SCIM',
    ],
    cta: 'Contact us',
    highlighted: false,
  },
];

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PricingPage() {
  const { t } = useTranslation();
  const [billing, setBilling] = useState<'annual' | 'monthly'>('annual');
  const [currency, setCurrency] = useState<Currency>('USD');
  const [currencyLoaded, setCurrencyLoaded] = useState(false);
  const { billing: userBilling } = useBilling();
  const isTrialing = userBilling.status === 'trialing';

  useEffect(() => {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY) as Currency | null;
    if (stored && stored in CURRENCY_CONFIG) {
      setCurrency(stored);
      setCurrencyLoaded(true);
      return;
    }
    fetch('/api/currency')
      .then(r => r.json())
      .then((data: { currency: string }) => {
        const detected = data.currency as Currency;
        if (detected in CURRENCY_CONFIG) setCurrency(detected);
      })
      .catch(() => {})
      .finally(() => setCurrencyLoaded(true));
  }, []);

  function handleCurrencyChange(next: Currency) {
    setCurrency(next);
    localStorage.setItem(CURRENCY_STORAGE_KEY, next);
  }

  const cfg = CURRENCY_CONFIG[currency];

  function getPriceInfo(planKey: string) {
    if (planKey === 'starter') {
      return {
        monthly: formatPrice(cfg.symbol, cfg.starterMonthly),
        monthlySubtext: 'Billed monthly',
        annual: formatPrice(cfg.symbol, cfg.starterAnnualMonthly),
        annualSubtext: `Billed ${formatPrice(cfg.symbol, cfg.starterAnnualYearly)}/yr`,
      };
    }
    return {
      monthly: formatPrice(cfg.symbol, cfg.proMonthly),
      monthlySubtext: 'Billed monthly',
      annual: formatPrice(cfg.symbol, cfg.proAnnualMonthly),
      annualSubtext: `Billed ${formatPrice(cfg.symbol, cfg.proAnnualYearly)}/yr`,
    };
  }

  return (
    <div className="landing">
      <PublicHeader />

      <main className="pricing-page">
        <div className="pricing-hero">
          <h1>{t('pricing.title')}</h1>
          <p className="pricing-subtitle">
            {isTrialing ? t('pricing.subtitleUpgrade') : t('pricing.subtitleTrial')}
          </p>

          <div className="billing-toggle">
            <button
              className={`billing-tab${billing === 'monthly' ? ' billing-tab--active' : ''}`}
              onClick={() => setBilling('monthly')}
            >
              {t('pricing.monthly')}
            </button>
            <button
              className={`billing-tab${billing === 'annual' ? ' billing-tab--active' : ''}`}
              onClick={() => setBilling('annual')}
            >
              {t('pricing.annual')}
              <span className="billing-save-badge">{t('pricing.save25')}</span>
            </button>
          </div>

          {currencyLoaded && (
            <div className="pricing-currency-row">
              <label htmlFor="currency-select" className="pricing-currency-label">
                {t('pricing.pricesShownIn')}
              </label>
              <select
                id="currency-select"
                className="pricing-currency-select"
                value={currency}
                onChange={e => handleCurrencyChange(e.target.value as Currency)}
              >
                {(Object.keys(CURRENCY_LABELS) as Currency[]).map(c => (
                  <option key={c} value={c}>{CURRENCY_LABELS[c]}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="pricing-cards">
          {PLANS.map(plan => {
            const isEnterprise = plan.name === 'Enterprise';
            const priceInfo = plan.planKey ? getPriceInfo(plan.planKey) : null;
            const price   = priceInfo ? (billing === 'annual' ? priceInfo.annual   : priceInfo.monthly)   : 'Get in Touch';
            const subtext = priceInfo ? (billing === 'annual' ? priceInfo.annualSubtext : priceInfo.monthlySubtext) : 'Custom pricing';

            const ctaHref = isEnterprise
              ? 'mailto:hello@theemailhelper.com'
              : `/pricing/checkout?plan=${plan.planKey}&cycle=${billing}&currency=${currency}`;

            return (
              <div
                key={plan.name}
                className={`pricing-card${plan.highlighted ? ' pricing-card--highlighted' : ''}`}
              >
                {plan.badge && (
                  <span className="pricing-card-badge">{plan.badge}</span>
                )}

                <div className="pricing-card-header">
                  <h2 className="pricing-card-name">{plan.name}</h2>
                  <p className="pricing-card-desc">{plan.description}</p>
                  {plan.trialBadge && !isTrialing && (
                    <span className="pricing-trial-badge">{plan.trialBadge}</span>
                  )}
                  <div className="pricing-card-price">
                    {isEnterprise ? (
                      <span className="pricing-price-text">{price}</span>
                    ) : (
                      <>
                        <span className="pricing-price-amount">{price}</span>
                        <span className="pricing-price-period">/mo</span>
                      </>
                    )}
                  </div>
                  <p className="pricing-price-subtext">{subtext}</p>
                </div>

                <ul className="pricing-features">
                  {plan.features.map(f => (
                    <li key={f} className="pricing-feature">
                      <span className="pricing-feature-icon"><CheckIcon /></span>
                      {f}
                    </li>
                  ))}
                </ul>

                {isEnterprise ? (
                  <a href={ctaHref} className="pricing-cta pricing-cta--secondary">
                    {plan.cta}
                  </a>
                ) : (
                  <Link
                    to={ctaHref}
                    className={`pricing-cta${plan.highlighted ? ' pricing-cta--primary' : ' pricing-cta--secondary'}`}
                  >
                    {isTrialing ? t('pricing.upgrade') : plan.cta}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </main>

      <footer className="landing-footer">
        {t('common.copyright', { year: new Date().getFullYear() })} ·{' '}
        <Link to="/privacy">{t('landing.footer.privacy')}</Link>
      </footer>
    </div>
  );
}
